import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { repo } from "../../dbservice/repo";
import { toast, Toaster } from "sonner";

const ServerCheck = () => {
  const [status, setStatus] = useState("checking"); // 'checking' | 'failed' | 'admin-paused'
  const [targetUrl, setTargetUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isRechecking, setIsRechecking] = useState(false);

  // 📊 PROGRESS & ERROR-METRICS STATE BINDINGS
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncAction, setSyncAction] = useState(
    "Initializing database links...",
  );
  const [roadblockDetails, setRoadblockDetails] = useState(null);

  // 🛠️ ADMIN CONTROL STATE
  const [isAdmin, setIsAdmin] = useState(false);
  const [isHealing, setIsHealing] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // 📝 HISTORICAL OPERATION TRACKING LOGS
  const [syncHistoryLogs, setSyncHistoryLogs] = useState([]);

  const navigate = useNavigate();
  const isMounted = useRef(true);

  const addHistoryLog = (message, type = "info") => {
    if (!isMounted.current) return;
    const timestamp = new Date().toLocaleTimeString();
    setSyncHistoryLogs((prev) => [
      ...prev,
      { text: message, time: timestamp, type },
    ]);
  };

  const handleSuccessfulRoute = (role) => {
    switch (role) {
      case "admin":
      case "locationAdmin":
        navigate("/admin");
        break;
      case "auditor":
        navigate("/auditor");
        break;
      case "user":
        navigate("/user");
        break;
      default:
        navigate("/login");
    }
  };

  const verifyAndRoute = async (isManualRetry = false) => {
    if (!isMounted.current) return;

    if (isManualRetry) {
      setIsRechecking(true);
      addHistoryLog("🔄 Re-probing local proxy connection...", "info");
    } else {
      setStatus("checking");
      setErrorMessage("");
      setSyncProgress(0);
      setRoadblockDetails(null);
      setSyncHistoryLogs([]);
    }

    // 1. Bind structural tracker event hooks out of the Repository engine instance
    repo.onProgressUpdate = (percentage, currentAction) => {
      if (!isMounted.current) return;
      setSyncProgress(percentage);
      setSyncAction(currentAction);

      addHistoryLog(`[${percentage}%] ${currentAction}`, "info");

      if (percentage === 100) {
        toast.success("Database fully optimized!", { id: "sync-progress" });
      } else if (percentage % 25 === 0) {
        toast.info(currentAction, { id: "sync-progress" });
      }
    };

    repo.onInitializationError = (errorReport) => {
      if (!isMounted.current) return;
      setRoadblockDetails(errorReport);
      addHistoryLog(
        `🛑 Roadblock: ${errorReport.phase} - ${errorReport.message}`,
        "error",
      );

      toast.error(`Roadblock: ${errorReport.phase}`, {
        description: errorReport.message,
      });
    };

    try {
      if (!isManualRetry) {
        addHistoryLog(
          "Reading cached user profiles and authorization schemas...",
          "info",
        );
      }

      const userDataRaw = localStorage.getItem("userData");
      if (!userDataRaw) {
        if (isMounted.current) navigate("/login");
        return;
      }
      const userData = JSON.parse(userDataRaw);
      setCurrentUserRole(userData.role);

      const isUserAdmin =
        userData.role === "admin" ||
        userData.role === "superadmin" ||
        userData.role === "locationAdmin";

      if (isUserAdmin) {
        setIsAdmin(true);
      }

      if (userData.role === "superadmin") {
        if (isMounted.current) navigate("/super-admin");
        return;
      }

      const companyDataRaw = localStorage.getItem("companyData");
      if (companyDataRaw) {
        const companyData = JSON.parse(companyDataRaw);
        if (isMounted.current) {
          setTargetUrl(companyData?.localSupabaseURL || "Not Configured");
          setCompanyId(companyData?.id || userData?.companyId || null);
        }
      }

      console.log("[SERVER CHECK] Commencing network probe on gateway node...");
      addHistoryLog(
        "Starting automated network heartbeat handshake probes...",
        "info",
      );

      const result = await repo.probeLocalServer();
      console.log("[SERVER CHECK] Network probe diagnostic payload:", result);

      if (!isMounted.current) return;

      if (result.success) {
        setIsRechecking(false);
        addHistoryLog("✅ Connection verified successfully!", "success");
        localStorage.setItem("localServerVerified", "true");

        // 🛑 IF ADMIN: Do NOT auto-route. Pause here and show admin control panel.
        if (isUserAdmin) {
          setStatus("admin-paused");
          addHistoryLog(
            "Paused for Admin review. Choose Healing or Proceed.",
            "info",
          );
          return;
        }

        // 🚀 IF NORMAL USER: Auto-route seamlessly as before
        setTimeout(() => {
          if (!isMounted.current) return;
          handleSuccessfulRoute(userData.role);
        }, 1000);
      } else {
        setIsRechecking(false);
        setErrorMessage(
          result.message || "Localized synchronization execution failed.",
        );
        addHistoryLog(
          "Verification failed. Local server unreachable.",
          "error",
        );
        setStatus("failed");
      }
    } catch (error) {
      console.error("[SERVER CHECK EXCEPTION]:", error);
      if (isMounted.current) {
        setIsRechecking(false);
        setErrorMessage(
          "An unexpected engine error occurred while initializing network probes.",
        );
        addHistoryLog(
          "System exception captured during initialization execution routine.",
          "error",
        );
        setStatus("failed");
      }
    }
  };

  // 🏥 Force Reconcile and Heal Execution Routine
  const handleAdminHeal = async (tableName = "assets") => {
    if (!companyId) {
      toast.error("Healing Failed", {
        description: "Company ID not resolved in active session context.",
      });
      return;
    }

    setIsHealing(true);
    addHistoryLog(
      `🚨 Initiating admin-forced healing on table: ${tableName}...`,
      "info",
    );
    toast.loading("Executing Database Reconciliation & Healing...", {
      id: "admin-heal",
    });

    try {
      const report = await repo.adminForceReconcileAndHeal(
        tableName,
        companyId,
      );

      addHistoryLog(
        `✅ Healing finished. Missing local restored: ${report.missingLocallyCount}, Missing cloud backed up: ${report.missingInCloudCount}`,
        "success",
      );

      toast.success("Healing Complete", {
        id: "admin-heal",
        description: `Restored ${report.missingLocallyCount} local records & pushed ${report.missingInCloudCount} cloud records.`,
      });

      verifyAndRoute(true);
    } catch (error) {
      console.error("[ADMIN HEAL EXCEPTION]:", error);
      addHistoryLog(`❌ Admin Heal Failed: ${error.message || error}`, "error");
      toast.error("Healing Routine Failed", {
        id: "admin-heal",
        description:
          error.message || "An error occurred during reconciliation.",
      });
    } finally {
      if (isMounted.current) {
        setIsHealing(false);
      }
    }
  };

  // 🔄 Initial Probe Lifecycle Hook
  useEffect(() => {
    isMounted.current = true;
    verifyAndRoute(false);

    return () => {
      isMounted.current = false;
      repo.onProgressUpdate = null;
      repo.onInitializationError = null;
    };
  }, []);

  useEffect(() => {
    const styleId = "material-design-3-animation-lines";
    if (!document.getElementById(styleId)) {
      const styleTag = document.createElement("style");
      styleTag.id = styleId;
      styleTag.innerText = `
        @keyframes m3Spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .m3-button-primary:hover { background-color: #1e3a8a !important; box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important; }
        .m3-button-secondary:hover { background-color: #f1f5f9 !important; }
        .m3-button-heal:hover { background-color: #991b1b !important; }
      `;
      document.head.appendChild(styleTag);
    }
  }, []);

  // 1. Initial Checking Screen
  if (status === "checking" && !isRechecking) {
    return (
      <div style={m3Styles.container}>
        <Toaster
          position="top-right"
          richColors
          closeButton
          duration={Infinity}
        />
        <div style={m3Styles.card}>
          <div style={m3Styles.spinner}></div>
          <h2 style={m3Styles.heading}>Verifying Connection</h2>

          <div style={m3Styles.progressContainer}>
            <div
              style={{ ...m3Styles.progressBar, width: `${syncProgress}%` }}
            ></div>
          </div>
          <div style={m3Styles.progressPercentage}>{syncProgress}%</div>

          <p style={m3Styles.text}>{syncAction}</p>

          {syncHistoryLogs.length > 0 && (
            <div style={m3Styles.logContainer}>
              <div style={m3Styles.logTitle}>System Lifecycle Audit Logs</div>
              <div style={m3Styles.logBox}>
                {syncHistoryLogs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      ...m3Styles.logLine,
                      color:
                        log.type === "error"
                          ? "#ef4444"
                          : log.type === "success"
                            ? "#16a34a"
                            : "#475569",
                    }}
                  >
                    <span style={m3Styles.logTime}>[{log.time}]</span>{" "}
                    {log.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 2. Admin Paused Success View (Admin Console Override)
  if (status === "admin-paused") {
    return (
      <div style={m3Styles.container}>
        <Toaster
          position="top-right"
          richColors
          closeButton
          duration={Infinity}
        />
        <div style={m3Styles.card}>
          <div
            style={{ ...m3Styles.errorBar, backgroundColor: "#16a34a" }}
          ></div>

          <div style={{ ...m3Styles.iconWrapper, backgroundColor: "#dcfce7" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="32px"
              viewBox="0 -960 960 960"
              width="32px"
              fill="#16a34a"
            >
              <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
            </svg>
          </div>

          <h2 style={m3Styles.heading}>Server Verified (Admin Mode)</h2>
          <p style={m3Styles.text}>
            System connection checks passed successfully. As an administrator,
            you can run a forced healing sync or proceed to your dashboard.
          </p>

          {isHealing && (
            <div
              style={{
                ...m3Styles.recheckingBanner,
                backgroundColor: "#fef2f2",
                borderColor: "#fca5a5",
                color: "#991b1b",
              }}
            >
              <div
                style={{
                  ...m3Styles.miniSpinner,
                  borderColor: "#fca5a5",
                  borderTopColor: "#991b1b",
                }}
              ></div>
              <span>Executing Admin Database Healing & Reconciliation...</span>
            </div>
          )}

          <div style={m3Styles.logContainer}>
            <div style={m3Styles.logTitle}>System Lifecycle Audit Logs</div>
            <div style={m3Styles.logBox}>
              {syncHistoryLogs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    ...m3Styles.logLine,
                    color:
                      log.type === "error"
                        ? "#ef4444"
                        : log.type === "success"
                          ? "#16a34a"
                          : "#94a3b8",
                  }}
                >
                  <span style={m3Styles.logTime}>[{log.time}]</span> {log.text}
                </div>
              ))}
            </div>
          </div>

          <div style={m3Styles.buttonGroup}>
            <button
              onClick={() => handleAdminHeal("assets")}
              className="m3-button-heal"
              style={m3Styles.healBtn}
              disabled={isHealing}
            >
              {isHealing ? "Healing..." : "Healing Sync"}
            </button>

            <button
              onClick={() => handleSuccessfulRoute(currentUserRole)}
              className="m3-button-primary"
              style={m3Styles.primaryBtn}
              disabled={isHealing}
            >
              Proceed to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Unreachable Error Dialog View (For failed states)
  return (
    <div style={m3Styles.container}>
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={Infinity}
      />
      <div style={m3Styles.card}>
        <div style={m3Styles.errorBar}></div>

        <div style={m3Styles.iconWrapper}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="32px"
            viewBox="0 -960 960 960"
            width="32px"
            fill="#dc2626"
          >
            <path d="M2 20v-10h12v10H2zm0-15v-10h12v10H2zm456 91L42 165q-18-9-27-26.5T6-99v-616q0-24 18-41.5t42-17.5h828q24 0 42 17.5t18 41.5v616q0 24-18 41.5T894-99L458 96zm0-116 376-515H124l334 515zm0-230q21 0 35.5-14.5T508-460q0-21-14.5-35.5T458-510q-21 0-35.5 14.5T408-460q0 21 14.5 35.5T458-410z" />
          </svg>
        </div>

        <h2 style={m3Styles.heading}>Local Server Unreachable</h2>

        {isRechecking && (
          <div style={m3Styles.recheckingBanner}>
            <div style={m3Styles.miniSpinner}></div>
            <span>Re-probing local server connection...</span>
          </div>
        )}

        {isHealing && (
          <div
            style={{
              ...m3Styles.recheckingBanner,
              backgroundColor: "#fef2f2",
              borderColor: "#fca5a5",
              color: "#991b1b",
            }}
          >
            <div
              style={{
                ...m3Styles.miniSpinner,
                borderColor: "#fca5a5",
                borderTopColor: "#991b1b",
              }}
            ></div>
            <span>Executing Admin Database Healing & Reconciliation...</span>
          </div>
        )}

        <p style={m3Styles.text}>{errorMessage}</p>

        {roadblockDetails && (
          <div style={m3Styles.diagnosticCard}>
            <div style={m3Styles.diagnosticTitle}>Technical Diagnostics</div>
            <div style={m3Styles.diagnosticRow}>
              <strong>Phase:</strong> {roadblockDetails.phase}
            </div>
            <div style={m3Styles.diagnosticRow}>
              <strong>Code:</strong> {roadblockDetails.code}
            </div>
            <div style={m3Styles.diagnosticDetails}>
              <strong>Reason:</strong> {roadblockDetails.details}
            </div>
          </div>
        )}

        <div style={m3Styles.logContainer}>
          <div style={m3Styles.logTitle}>System Lifecycle Audit Logs</div>
          <div style={m3Styles.logBox}>
            {syncHistoryLogs.map((log, index) => (
              <div
                key={index}
                style={{
                  ...m3Styles.logLine,
                  color:
                    log.type === "error"
                      ? "#ef4444"
                      : log.type === "success"
                        ? "#16a34a"
                        : "#94a3b8",
                }}
              >
                <span style={m3Styles.logTime}>[{log.time}]</span> {log.text}
              </div>
            ))}
          </div>
        </div>

        <div style={m3Styles.urlContainer}>
          <span style={m3Styles.urlLabel}>Target Node URL</span>
          <span style={m3Styles.urlValue}>{targetUrl}</span>
        </div>

        <div style={m3Styles.adminNotice}>
          <strong>Notice for System Administrators:</strong> Verify that your
          local Docker cluster or Caddy server is running and bound to{" "}
          <code>supabase.local</code>.
        </div>

        <div style={m3Styles.buttonGroup}>
          <button
            onClick={() => {
              // 1. Preserve the sync queues before wiping
              const localQueue = localStorage.getItem("local_sync_queue");
              const cloudQueue = localStorage.getItem("cloud_sync_outbox");

              // 2. Perform the full clear
              localStorage.clear();

              // 3. Restore the sync queues so pending data isn't lost
              if (localQueue !== null) {
                localStorage.setItem("local_sync_queue", localQueue);
              }
              if (cloudQueue !== null) {
                localStorage.setItem("cloud_sync_outbox", cloudQueue);
              }

              navigate("/login");
            }}
            className="m3-button-secondary"
            style={m3Styles.secondaryBtn}
            disabled={isHealing || isRechecking}
          >
            Back to Login
          </button>

          {isAdmin && (
            <button
              onClick={() => handleAdminHeal("assets")}
              className="m3-button-heal"
              style={m3Styles.healBtn}
              disabled={isHealing || isRechecking}
            >
              {isHealing ? "Healing..." : "Healing Sync"}
            </button>
          )}

          <button
            onClick={() => verifyAndRoute(true)}
            className="m3-button-primary"
            style={m3Styles.primaryBtn}
            disabled={isRechecking || isHealing}
          >
            {isRechecking ? "Probing..." : "Verify Again"}
          </button>
        </div>
      </div>
    </div>
  );
};

const m3Styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    fontFamily: '"Roboto", "Segoe UI", system-ui, sans-serif',
    padding: "2rem 1rem",
  },
  card: {
    position: "relative",
    background: "#ffffff",
    boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.15)",
    borderRadius: "16px",
    padding: "2.5rem 2rem",
    maxWidth: "36rem",
    width: "100%",
    textAlign: "center",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  errorBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    backgroundColor: "#b91c1c",
  },
  iconWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "64px",
    height: "64px",
    backgroundColor: "#fee2e2",
    borderRadius: "50%",
    marginBottom: "1rem",
    marginTop: "0.5rem",
  },
  heading: {
    fontSize: "1.5rem",
    fontWeight: "600",
    color: "#1e293b",
    margin: "0.5rem 0 0.75rem 0",
    letterSpacing: "-0.01em",
  },
  text: {
    color: "#64748b",
    fontSize: "0.875rem",
    lineHeight: "1.6",
    margin: "0 0 1.5rem 0",
  },
  recheckingBanner: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    padding: "0.5rem 1rem",
    borderRadius: "100px",
    fontSize: "0.8rem",
    fontWeight: "600",
    marginBottom: "1rem",
  },
  miniSpinner: {
    width: "14px",
    height: "14px",
    border: "2px solid #bfdbfe",
    borderTop: "2px solid #1d4ed8",
    borderRadius: "50%",
    animation: "m3Spin 0.75s linear infinite",
  },
  progressContainer: {
    width: "100%",
    height: "4px",
    backgroundColor: "#e2e8f0",
    borderRadius: "2px",
    overflow: "hidden",
    margin: "1rem 0 0.5rem 0",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#2563eb",
    transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  progressPercentage: {
    fontSize: "0.875rem",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "0.5rem",
  },
  logContainer: {
    marginTop: "1.5rem",
    marginBottom: "1.5rem",
    textAlign: "left",
    backgroundColor: "#0f172a",
    borderRadius: "12px",
    padding: "1rem",
    border: "1px solid #1e293b",
  },
  logTitle: {
    fontSize: "0.75rem",
    fontWeight: "700",
    textTransform: "uppercase",
    color: "#94a3b8",
    letterSpacing: "0.05em",
    marginBottom: "0.5rem",
    borderBottom: "1px solid #1e293b",
    paddingBottom: "0.25rem",
  },
  logBox: {
    maxHeight: "160px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
  },
  logLine: {
    fontSize: "0.8rem",
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    lineHeight: "1.4",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  },
  logTime: {
    color: "#64748b",
    marginRight: "0.5rem",
  },
  diagnosticCard: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "1rem",
    textAlign: "left",
    marginBottom: "1.5rem",
    fontSize: "0.825rem",
  },
  diagnosticTitle: {
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    marginBottom: "0.5rem",
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: "0.25rem",
  },
  diagnosticRow: {
    color: "#334155",
    marginBottom: "0.25rem",
  },
  diagnosticDetails: {
    color: "#ef4444",
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    marginTop: "0.5rem",
    wordBreak: "break-all",
  },
  urlContainer: {
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: "12px",
    padding: "0.875rem 1rem",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.25rem",
    textAlign: "left",
  },
  urlLabel: {
    fontSize: "0.7rem",
    fontWeight: "700",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  urlValue: {
    fontSize: "0.875rem",
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    color: "#0f172a",
    wordBreak: "break-all",
  },
  adminNotice: {
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "12px",
    padding: "1rem",
    fontSize: "0.825rem",
    color: "#166534",
    lineHeight: "1.5",
    textAlign: "left",
    marginBottom: "2rem",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #2563eb",
    borderRadius: "50%",
    margin: "1.5rem auto",
    animation: "m3Spin 0.85s cubic-bezier(0.4, 0, 0.2, 1) infinite",
  },
  buttonGroup: {
    display: "flex",
    gap: "0.5rem",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  primaryBtn: {
    backgroundColor: "#2563eb",
    color: "#ffffff",
    border: "none",
    borderRadius: "100px",
    padding: "0.625rem 1.25rem",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    transition: "all 0.2s ease-in-out",
  },
  secondaryBtn: {
    backgroundColor: "transparent",
    border: "1px solid #cbd5e1",
    color: "#475569",
    borderRadius: "100px",
    padding: "0.625rem 1.25rem",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
  },
  healBtn: {
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "100px",
    padding: "0.625rem 1.25rem",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    transition: "all 0.2s ease-in-out",
  },
};

export default ServerCheck;
